import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Search, X, Loader2, Sparkles } from "lucide-react";
import { useDispatch } from "react-redux";
import { setSelectedCatId } from "../slices/menuSlice";
import { fetchItemDetails } from "../slices/itemSlice";

const SearchModal = ({ onClose }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const dispatch = useDispatch();

  // 🔥 store request controller
  const controllerRef = useRef(null);

  /* ------------------ DEBOUNCE ------------------ */
  useEffect(() => {
    const handler = setTimeout(() => {
      if (query.trim().length >= 3) {
        setDebouncedQuery(query.trim());
      } else {
        setDebouncedQuery("");
        setResults([]);
      }
    }, 500); // 👈 debounce time

    return () => clearTimeout(handler);
  }, [query]);

  /* ------------------ SEARCH TRIGGER ------------------ */
  useEffect(() => {
    if (!debouncedQuery) return;

    handleSearch(debouncedQuery);

    // cancel running request when query changes
    return () => {
      if (controllerRef.current) {
        controllerRef.current.abort();
      }
    };
  }, [debouncedQuery]);

  /* ------------------ API SEARCH ------------------ */
  const handleSearch = async (text) => {
    try {
      // cancel previous request
      if (controllerRef.current) {
        controllerRef.current.abort();
      }

      const controller = new AbortController();
      controllerRef.current = controller;

      setLoading(true);

      const res = await axios.post(
        `${window.BASE_API_URL}globalsearch`,
        { text },
        { signal: controller.signal }
      );

      setResults(res.data?.data || []);
    } catch (err) {
      if (err.name !== "CanceledError") {
        console.error("Search error:", err);
      }
    } finally {
      setLoading(false);
    }
  };

  /* ------------------ CLICK HANDLERS ------------------ */
  const handleCategoryClick = (categoryId) => {
    dispatch(setSelectedCatId(categoryId));
    onClose();
  };

  const handleResultClick = (item) => {
    dispatch(fetchItemDetails(item.id));
    window.dispatchEvent(new Event("openItemModal"));
    dispatch(setSelectedCatId(item.category_id));
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-50 animate-fadeIn">
      <div className="bg-white/95 rounded-3xl shadow-2xl w-full max-w-2xl p-6 relative overflow-hidden animate-slideUp border border-amber-100">
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute right-5 top-5 text-gray-500 hover:text-amber-600 transition"
        >
          <X size={24} />
        </button>

        {/* Header */}
        <div className="flex items-center justify-center mb-5">
          <Sparkles className="text-amber-500 mr-2 animate-pulse" size={22} />
          <h2 className="text-lg font-bold text-gray-800 tracking-wide">
            Explore Your Favorites
          </h2>
        </div>

        {/* Search Input */}
        <div className="flex items-center gap-3 bg-white border-2 border-amber-200 rounded-full px-4 py-3 shadow-sm hover:shadow-md transition-all">
          <Search size={20} className="text-amber-600" />
          <input
            type="text"
            placeholder="Type at least 3 letters to search..."
            className="flex-1 bg-transparent outline-none text-sm text-gray-800"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {loading && <Loader2 size={20} className="animate-spin text-amber-600" />}
        </div>

        {/* Results */}
        <div className="mt-6 max-h-80 overflow-y-auto custom-scrollbar pr-1">
          {!loading && results.length === 0 && query.length >= 3 && (
            <p className="text-gray-400 text-sm text-center py-6">
              No matching items found 😔
            </p>
          )}

          {!loading && query.length < 3 && (
            <p className="text-gray-400 text-sm text-center py-6">
              Start typing at least{" "}
              <span className="font-semibold text-amber-500">3 letters</span>...
            </p>
          )}

          {results.map((item, index) => (
            <div
              key={index}
              onClick={() => handleResultClick(item)}
              className="group flex justify-between items-center px-4 py-3 rounded-xl mb-2 bg-gradient-to-r from-amber-50 to-white border border-amber-100 hover:from-amber-100 hover:to-amber-50 hover:border-amber-300 cursor-pointer transition-all duration-200"
            >
              <div>
                <p className="text-gray-800 font-medium group-hover:text-amber-700 transition">
                  {item.menu_name}
                </p>

                <div className="flex gap-2 mt-1 flex-wrap">
                  {item.category_name && (
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCategoryClick(item.category_id);
                      }}
                      className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 cursor-pointer hover:bg-blue-200"
                    >
                      {item.category_name}
                    </span>
                  )}

                  {item.subcategory_name && (
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCategoryClick(item.category_id);
                      }}
                      className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 cursor-pointer hover:bg-purple-200"
                    >
                      {item.subcategory_name}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Animations + scrollbar */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(40px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .animate-fadeIn { animation: fadeIn 0.3s ease-out forwards; }
        .animate-slideUp { animation: slideUp 0.35s ease-out forwards; }
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #fbbf24;
          border-radius: 10px;
        }
      `}</style>
    </div>
  );
};

export default SearchModal;
